import React, { useState } from 'react';
import mbxGeocoder, { GeocodeFeature, GeocodeRequest, GeocodeResponse } from '@mapbox/mapbox-sdk/services/geocoding';
import { Search, ISuggests } from '..';
import { SdkConfig } from '@mapbox/mapbox-sdk/lib/classes/mapi-client';
const mbxClient = require('@mapbox/mapbox-sdk');

/**
 * Mapbox geolookup init
 */

/**
 * ## geoCodingLookup
 * Lookup geo locations with Mapbox API.
 * @param searchInput The input value to be processed
 * @param countries The country search result boundary
 * @param limit The number of results to return
 */
export const geoLookup = (baseClient: SdkConfig, searchInput: string, countries: string[], searchLanguage?: string, limit = 5): Promise<GeocodeFeature[]> => {
	const geocoderService = mbxGeocoder(baseClient);
	return new Promise((resolve, reject) => {
		try {
			const request: GeocodeRequest = {
				query: searchInput,
				mode: 'mapbox.places',
				countries: countries,
				language: [searchLanguage ? searchLanguage : 'en-GB'],
				limit,
			};
			geocoderService
				.forwardGeocode(request)
				.send()
				.then((response) => {
					const match: GeocodeResponse = response.body;
					const features: GeocodeFeature[] = match.features;
					resolve(features);
				});
		} catch (error) {
			reject(error);
		}
	});
};

/**
 * ## IGeoSearch
 * @param accessToken Mapbox accessToken
 * @param language Language to be used when displaying search results
 * @param searchCountries The country lookup boundary (can be multiple).
 * * You can find a list of ISO country codes [here](https://www.ncbi.nlm.nih.gov/books/NBK7249/)
 * @param searchLimit The number of results to display
 * @param mobile Set mobile boolean
 * @param tagRender Custom render the tags attached to resulting objects
 * @param handleResultClick The click event action handler
 * @param className modify already set stylesheet
 */
export interface IGeoSearch {
	accessToken: string;
	searchLanguage?: string;
	searchCountries?: string[];
	searchLimit?: number;
	searchPlaceholder?: string;
	mobile?: boolean;
	className?: string | React.ReactNode;
	tagRender?: (tag: string) => string;
	handleChange?: (input?: string) => void;
	handleResultClick?: (key?: string) => void;
}

/**
 * ## GeoSearch
 * Modified @see Search component
 * @example <GeoSearch
				accessToken={'someApiString'}
				searchPlaceholder={'Find greenland address'}
				key={'geosearch-key'}
				searchCountries={['gl']}
			/>
 * @param props see {@link IGeoSearch}
 * @returns geoLookup() results.
 */
export const GeoSearch: React.FC<IGeoSearch> = (props) => {
	const [getGeoResultState, setGeoResultState] = useState<ISuggests[]>();
	const baseClient = new mbxClient({ accessToken: props.accessToken });

	/**
	 * ## handleResultClick
	 * Result click action handler
	 * Clear results on click
	 * @param key The clicked key
	 */
	const handleResultClick = (key?: string) => {
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
		props.handleResultClick?.(key);
		setGeoResultState(undefined);
	};

	/**
	 * ## handleOnChange
	 * Called everytime input value changes in searchbar
	 * @param input Current input value to be processed
	 */
	const handleOnChange = (input?: string) => {
		/* If handleChange prop defined, use. Else use default handlechange */
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
		if (props.handleChange) {
			props.handleChange(input);
		} else {
			if (input && input.length > 0) {
				// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
				geoLookup(baseClient, input, props.searchCountries ? props.searchCountries : [], props.searchLanguage, props.searchLimit).then((features) => {
					parseResult(features);
				});
			} else {
				setGeoResultState([]);
			}
		}
	};

	/**
	 * ## parseResult
	 * Parse the resulting features from @see GeocodeFeature[] to @see ISuggests[]
	 * Pass the result to <Search>
	 * @param results The lookup results.
	 */
	const parseResult = async (results: GeocodeFeature[]) => {
		const parsedResults: ISuggests[] = results.map((item) => {
			const tags: string[] = item.place_type.map((tag: string) => {
				// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
				return props.tagRender?.(tag) ?? tag;
			});
			return { key: item.id, title: item.text, description: item.place_name, tags: tags };
		});
		setGeoResultState(parsedResults);
	};

	return (
		<>
			<Search
				className={props.className}
				key={'search-input'}
				placeholder={props.searchPlaceholder}
				suggests={getGeoResultState}
				onRecordClick={handleResultClick}
				mobile={props.mobile}
				onSearch={handleOnChange}
			/>
		</>
	);
};
